import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { userAPI, authAPI, roleAPI } from '../services/api';

function getInitials(name) {
  if (!name) return '?';
  return name.trim().charAt(0).toUpperCase();
}

function checkPasswordStrength(password) {
  let score = 0;
  const feedback = [];
  if (password.length >= 8) score += 1; else feedback.push('At least 8 characters');
  if (/[a-z]/.test(password)) score += 1; else feedback.push('Include lowercase letters');
  if (/[A-Z]/.test(password)) score += 1; else feedback.push('Include uppercase letters');
  if (/[0-9]/.test(password)) score += 1; else feedback.push('Include numbers');
  if (password.length >= 12) score += 1;
  return { score, feedback };
}

function strengthColor(score) {
  if (score <= 1) return '#ef4444';
  if (score <= 2) return '#f59e0b';
  if (score <= 3) return '#3b82f6';
  return '#10b981';
}

function strengthText(score) {
  if (score <= 1) return 'Weak';
  if (score <= 2) return 'Fair';
  if (score <= 3) return 'Good';
  return 'Strong';
}

export default function Profile() {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({ username: '', role: '', is_active: true });
  const [original, setOriginal] = useState(null);
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);

  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [strength, setStrength] = useState({ score: 0, feedback: [] });

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    if (stored) {
      setUser(stored);
      const init = {
        username: stored.username || '',
        role: stored.role || '',
        is_active: stored.is_active !== false
      };
      setForm(init);
      setOriginal(init);
    }
    roleAPI.getAll().then(res => {
      const data = res.data || res;
      if (Array.isArray(data)) setRoles(data);
    }).catch(() => {});
  }, []);

  if (!user) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>Loading profile…</div>
      </div>
    );
  }

  const dirty = JSON.stringify(form) !== JSON.stringify(original);
  const isSelf = true; // this page always edits the logged-in user
  const isAdmin = user.role === 'admin';

  const updateField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (isSelf && form.role !== original.role) {
      toast.error('You cannot change your own role. Ask another administrator.');
      setForm(f => ({ ...f, role: original.role }));
      return;
    }
    if (isSelf && form.is_active === false) {
      toast.error('You cannot deactivate your own account.');
      setForm(f => ({ ...f, is_active: true }));
      return;
    }
    setSaving(true);
    try {
      const payload = { username: form.username };
      if (isAdmin) {
        payload.role = form.role;
        payload.is_active = form.is_active;
      }
      await userAPI.update(user.id, payload);
      const updated = { ...user, username: form.username, role: form.role, is_active: form.is_active };
      localStorage.setItem('user', JSON.stringify(updated));
      toast.success('Profile updated successfully');
      setOriginal({ username: form.username, role: form.role, is_active: form.is_active });
      setUser(updated);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePwChange = (e) => {
    const value = e.target.value;
    setPw(p => ({ ...p, newPassword: value }));
    setStrength(checkPasswordStrength(value));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pw.newPassword !== pw.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (pw.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPwLoading(true);
    try {
      await authAPI.changePassword({ currentPassword: pw.currentPassword, newPassword: pw.newPassword });
      toast.success('Password changed successfully');
      setPw({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setStrength({ score: 0, feedback: [] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const fmtDate = (v) => v ? new Date(v).toLocaleString() : '—';

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Profile</h1>
          <p className="page-subtitle">Manage your account details and security settings</p>
        </div>
      </div>

      <div className="profile-grid">
        {/* Account Information */}
        <div className="card profile-main">
          <div className="profile-head">
            <div className="profile-avatar">{getInitials(user.username)}</div>
            <div>
              <div className="profile-name">{user.username}</div>
              <div className="profile-sub">{user.role === 'admin' ? 'Administrator' : (roles.find(r => r.name === user.role)?.display_name || user.role)}</div>
            </div>
          </div>

          <div className="card-header" style={{ marginTop: 12 }}>
            <div><h2 className="card-title">Account Information</h2><p className="card-subtitle">Update your personal account details</p></div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Username</label>
              <input
                className="form-input"
                value={form.username}
                onChange={e => updateField('username', e.target.value)}
                placeholder="Username"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <div className="locked-field">
                <input className="form-input" value={user.email || '—'} disabled readOnly />
                <span className="locked-note">Locked — contact an administrator to change email</span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Role</label>
              <select
                className="form-input"
                value={form.role}
                onChange={e => updateField('role', e.target.value)}
                disabled={isSelf}
              >
                {roles.length === 0 && <option value={form.role}>{form.role}</option>}
                {roles.map(r => (
                  <option key={r.name} value={r.name}>{r.display_name || r.name}</option>
                ))}
              </select>
              {isSelf && <span className="locked-note">Your role is managed by administrators</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Account Status</label>
              <div className="toggle-row">
                <button
                  type="button"
                  className={`switch ${form.is_active ? 'on' : ''}`}
                  onClick={() => updateField('is_active', !form.is_active)}
                  aria-pressed={form.is_active}
                  disabled={!isAdmin}
                  style={!isAdmin ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                >
                  <span className="switch-knob" />
                </button>
                <span className={`badge ${form.is_active ? 'badge-success' : 'badge-warning'}`}>
                  {form.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              {!isAdmin && (
                <span className="locked-note">Status is managed by administrators</span>
              )}
              {isSelf && isAdmin && form.is_active === false && (
                <span className="locked-note" style={{ color: '#ef4444' }}>You cannot deactivate your own account</span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            {dirty && (
              <button className="btn btn-outline" onClick={() => setForm(original)} disabled={saving}>
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Side column */}
        <div className="profile-side">
          {/* Security */}
          <div className="card">
            <div className="card-header">
              <div><h2 className="card-title">Security</h2><p className="card-subtitle">Change your account password</p></div>
            </div>
            <form onSubmit={handleChangePassword} className="profile-pw-form">
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input
                  type={show.current ? 'text' : 'password'}
                  className="form-input"
                  value={pw.currentPassword}
                  onChange={e => setPw(p => ({ ...p, currentPassword: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type={show.next ? 'text' : 'password'}
                  className="form-input"
                  value={pw.newPassword}
                  onChange={handlePwChange}
                  required
                />
                {pw.newPassword && (
                  <div className="pw-strength">
                    <div className="pw-strength-bar">
                      <div style={{ width: `${(strength.score / 5) * 100}%`, background: strengthColor(strength.score) }} />
                    </div>
                    <div className="pw-strength-label" style={{ color: strengthColor(strength.score) }}>
                      {strengthText(strength.score)}
                    </div>
                    {strength.feedback.length > 0 && (
                      <ul className="pw-feedback">
                        {strength.feedback.map((t, i) => <li key={i}>{t}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input
                  type={show.confirm ? 'text' : 'password'}
                  className={`form-input ${pw.confirmPassword && (pw.newPassword !== pw.confirmPassword ? 'error' : 'success')}`}
                  value={pw.confirmPassword}
                  onChange={e => setPw(p => ({ ...p, confirmPassword: e.target.value }))}
                  required
                />
                {pw.confirmPassword && pw.newPassword !== pw.confirmPassword && (
                  <span className="locked-note" style={{ color: '#ef4444' }}>Passwords do not match</span>
                )}
              </div>
              <button type="submit" className="btn btn-primary" disabled={pwLoading || pw.newPassword !== pw.confirmPassword || pw.newPassword.length < 8}>
                {pwLoading ? 'Updating…' : 'Update Password'}
              </button>
            </form>
          </div>

          {/* Account details */}
          <div className="card">
            <div className="card-header">
              <div><h2 className="card-title">Account Details</h2></div>
            </div>
            <div className="detail-list">
              <div className="detail-row"><span>User ID</span><span>{user.id}</span></div>
              <div className="detail-row"><span>Email</span><span>{user.email || '—'}</span></div>
              <div className="detail-row"><span>Role</span><span>{user.role}</span></div>
              <div className="detail-row"><span>Must Change Password</span><span>{user.mustChangePassword ? 'Yes' : 'No'}</span></div>
              <div className="detail-row"><span>Created</span><span>{fmtDate(user.created_at)}</span></div>
              <div className="detail-row"><span>Last Updated</span><span>{fmtDate(user.updated_at)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
